/**
 * Este módulo é a costura da migração das integrações para o Zoraxy.
 *
 * O que já foi migrado passa por `zoraxy()`: o navegador fala com o próprio
 * Chatwoot, que valida e só então aciona o Aceleres como executor. O que ainda
 * não foi passa por `aceleres()`, o caminho antigo — o navegador chamando
 * `homolog.aceleres.ai` direto com o cookie de sessão do Aceleres, o que exige
 * a pessoa logada nos dois sistemas no mesmo navegador. Cada fatia da migração
 * move um punhado de chamadas de baixo para cima nesta lista, e a tela não
 * precisa saber de nada disso.
 */
import Auth from '../auth';

const accountId = () => {
  if (window.location.pathname.includes('/app/accounts')) {
    return window.location.pathname.split('/')[3];
  }

  return '';
};

/**
 * O Chatwoot autentica a API por devise_token_auth, não pelo cookie de sessão.
 *
 * Quem monta esses cabeçalhos normalmente é o `wootApi` do APIHelper, a partir
 * do cookie `cw_d_session_info`. Um `fetch` cru não passa por ele: sem os
 * cabeçalhos abaixo, o `authenticate_user!` derruba a requisição antes do
 * controller e devolve 401 com `{errors:[…]}` — um corpo sem `message`, que caía
 * no texto genérico "Não foi possível concluir esta operação de integração.".
 *
 * Era isso que deixava a tela de integrações do Zoraxy sem nenhuma conexão
 * WhatsApp enquanto o Aceleres mostrava as mesmas instâncias normalmente
 * (homolog 2026-08-10 13:21:03: `Filter chain halted as :authenticate_user!`,
 * `Completed 401 Unauthorized`).
 */
const authHeaders = () => {
  const authData = Auth.getAuthData();

  if (!authData) {
    return {};
  }

  const {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  } = authData;

  return {
    'access-token': accessToken,
    'token-type': tokenType,
    client,
    expiry,
    uid,
  };
};

const zoraxyRequest = async (path, { method = 'GET', body } = {}) => {
  const response = await fetch(
    `/api/v1/accounts/${accountId()}/aceleres/whatsapp_instances${path}`,
    {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...authHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    }
  );
  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    throw new Error(
      data.message || 'Não foi possível concluir esta operação de integração.'
    );
  }

  return data;
};

const resolveAceleresBase = () => {
  const host = window.location.host;

  if (host === 'atendimento.zoraxy.ai' || host.includes('homolog')) {
    return 'https://homolog.aceleres.ai';
  }

  return 'https://aceleres.ai';
};

const request = async (path, { method = 'GET', body } = {}) => {
  const response = await fetch(
    `${resolveAceleresBase()}/api/integracoes${path}`,
    {
      method,
      credentials: 'include',
      headers: body
        ? { Accept: 'application/json', 'Content-Type': 'application/json' }
        : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }
  );
  const data = await response.json().catch(() => ({}));

  if (!response.ok || data.success === false) {
    throw new Error(
      data.message || 'Não foi possível concluir esta operação de integração.'
    );
  }

  return data;
};

/**
 * O status vem por `api/integracoes/*` de propósito.
 *
 * O caminho anterior (`/whatsapp-import/api/instances/{id}/status`) está fora
 * de `config/cors.php`, que só libera `api/*`. O servidor respondia, mas o
 * navegador bloqueava a leitura da resposta: o polling deste hub falhava em
 * silêncio, o QR nunca fechava sozinho e um card desatualizado só se corrigia
 * recarregando a página.
 *
 * Diferente das demais, esta chamada devolve o corpo mesmo em erro: 503 com
 * `retryable` significa "provedor oscilando, continue tentando", e transformar
 * isso em exceção descartaria um pareamento em andamento.
 */
const getWhatsappStatus = async id => {
  const response = await fetch(
    `/api/v1/accounts/${accountId()}/aceleres/whatsapp_instances/${id}/status`,
    {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...authHeaders() },
    }
  );
  const data = await response.json().catch(() => ({}));

  if (!response.ok && !data.retryable) {
    throw new Error(
      data.message || 'Não foi possível consultar o estado da conexão WhatsApp.'
    );
  }

  return { ...data, http_status: response.status };
};

const oauthUrl = path => {
  const returnTo = window.location.href;
  return `${resolveAceleresBase()}${path}?google_disclosure=1&return_to=${encodeURIComponent(returnTo)}`;
};

export default {
  // ── Já migrados: validação no Zoraxy, Aceleres como executor ──────────────
  getWhatsapp: () => zoraxyRequest(''),
  getWhatsappStatus,
  getWhatsappSellers: () => zoraxyRequest('/sellers'),
  createWhatsapp: payload =>
    zoraxyRequest('', { method: 'POST', body: payload }),
  connectWhatsapp: (id, payload = {}) =>
    zoraxyRequest(`/${id}/connect`, { method: 'POST', body: payload }),
  getWhatsappQr: id => zoraxyRequest(`/${id}/qrcode`),
  // Fechar o QR não é o mesmo que desconectar: aqui a sessão só é apagada se o
  // estado remoto provar que ninguém leu o código. `disconnect` derrubaria uma
  // sessão já pareada e devolveria a chave ao pool.
  cancelConnectWhatsapp: id =>
    zoraxyRequest(`/${id}/cancel_connect`, { method: 'POST', body: {} }),
  disconnectWhatsapp: id =>
    zoraxyRequest(`/${id}/disconnect`, { method: 'POST', body: {} }),
  // Abrir mão da reserva de 7 dias criada por uma queda espontânea. Diferente
  // de desconectar: a conexão já está fora do ar, o que se devolve é a vaga.
  releaseWhatsappKey: id =>
    zoraxyRequest(`/${id}/release_key`, { method: 'POST', body: {} }),

  renameWhatsapp: (id, payload) =>
    zoraxyRequest(`/${id}`, { method: 'PATCH', body: payload }),
  setWhatsappPrimary: id =>
    zoraxyRequest(`/${id}/primary`, { method: 'PATCH', body: {} }),
  getWhatsappDeletionPreview: id => zoraxyRequest(`/${id}/deletion_preview`),
  deleteWhatsapp: id => zoraxyRequest(`/${id}`, { method: 'DELETE' }),
  updateWhatsappType: (id, payload) =>
    zoraxyRequest(`/${id}/type`, { method: 'PATCH', body: payload }),
  getWhatsappGroups: id => zoraxyRequest(`/${id}/groups`),
  updateWhatsappGroups: (id, payload) =>
    zoraxyRequest(`/${id}/groups`, { method: 'PATCH', body: payload }),
  syncWhatsappGroups: id =>
    zoraxyRequest(`/${id}/groups/sync`, { method: 'POST', body: {} }),
  getWhatsappPreload: id => zoraxyRequest(`/${id}/preload`),
  updateWhatsappPreload: (id, payload) =>
    zoraxyRequest(`/${id}/preload`, { method: 'PATCH', body: payload }),
  getWhatsappMigration: id => zoraxyRequest(`/${id}/migration`),
  confirmWhatsappMigration: (id, payload = {}) =>
    zoraxyRequest(`/${id}/migration/confirm`, {
      method: 'POST',
      body: payload,
    }),
  cancelWhatsappMigration: (id, payload = {}) =>
    zoraxyRequest(`/${id}/migration/cancel`, {
      method: 'POST',
      body: payload,
    }),

  // ── Ainda no caminho antigo, migram na próxima fatia ──────────────────────
  getEmail: () => request('/email'),
  createEmail: payload => request('/email', { method: 'POST', body: payload }),
  testEmail: id => request(`/email/${id}/test`, { method: 'POST', body: {} }),
  deleteEmail: id => request(`/email/${id}`, { method: 'DELETE' }),
  getEmailProviders: () => request('/providers'),
  getSocialChannels: () => zoraxyRequest('/social_channels'),
  syncSocialChannels: () =>
    zoraxyRequest('/social_channels/sync', { method: 'POST', body: {} }),
  getGmail: () => request('/google/gmail'),
  disconnectGmail: () =>
    request('/google/gmail/disconnect', { method: 'POST', body: {} }),
  gmailOAuthUrl: () => oauthUrl('/gmail-oauth/redirect'),
};
